import "./viewer.css";
import { h, type Child } from "../shared/safe";

// One preview viewer for BOTH the synthetic template demos (Template Studio) and the client's actual builds (Site screen).
//
// Isolation, stated once: every page of a previewed site is shown in an iframe that is a DIRECT child of the dashboard with `sandbox=""` (no scripts,
// no forms, no popups, no same-origin: an opaque origin). The only ancestor of such a frame is the dashboard, which the API's `frame-ancestors`
// (self + the configured dashboard origins) allows, whether the dashboard and the API share an origin or not. The all-pages overview is therefore
// a grid of such frames owned by the dashboard, NOT the API's overview document with frames nested inside a sandboxed frame (an opaque-origin
// ancestor never matches `frame-ancestors`, which is why that nesting was refused by the browser). No `allow-scripts allow-same-origin` pair is ever used.

export type ViewerKind = "demo" | "client";
export interface ViewerPage { id: string; title: string; group?: string; src: string }
export interface ViewerState { mode: "site" | "pages"; device: "desktop" | "mobile"; fit: "fit" | "actual"; tiles: "s" | "m" | "l"; pageId: string }
export interface ViewerOptions {
  kind: ViewerKind;
  /** Prefix for stable element ids and data attributes ("st" for template demos, "sk-<siteId>" for a client build). */
  idPrefix: string;
  /** Site name shown in the label of the frames (accessible titles). */
  name: string;
  siteSrc: string;
  pages: readonly ViewerPage[];
  /** Short truthful statement of what is being previewed (shown above the viewer, with a kind badge). */
  notice: Child;
  /** Label of the live mode: "Live demo" or "Live site". */
  liveLabel: string;
  state?: Partial<ViewerState>;
  onState?: (s: ViewerState) => void;
  /** Extra controls placed in the toolbar (for example Close, or the new-tab link). */
  extra?: readonly Child[];
  /** Shown in place of the pages once the preview credential has expired; the viewer disposes itself. */
  expiredMessage?: Child;
}
export interface ViewerHandle { el: HTMLElement; state(): ViewerState; show(pageId: string): void; expire(): void; destroy(): void }

const DESKTOP_W = 1280;
const MOBILE_W = 390;
const TILE_FRAME_H = { desktop: 1500, mobile: 1000 } as const;
const TILE_COLS: Record<ViewerState["tiles"], string> = { s: "minmax(min(100%, 240px), 1fr)", m: "minmax(min(100%, 340px), 1fr)", l: "minmax(min(100%, 560px), 1fr)" };

function choice(label: string, options: readonly { value: string; label: string }[], value: string, onChange: (v: string) => void, attrs: Record<string, string> = {}): { el: HTMLElement; set(v: string): void } {
  let current = value;
  const el = h("div", { class: "pv-choice", role: "radiogroup", "aria-label": label, dataset: attrs });
  const draw = (focus?: string) => {
    el.replaceChildren(...options.map((o) => {
      const on = o.value === current;
      const b = h("button", { type: "button", class: `pv-choice__opt${on ? " on" : ""}`, role: "radio", "aria-checked": String(on), tabindex: on ? "0" : "-1", dataset: { value: o.value } }, o.label) as HTMLButtonElement;
      b.addEventListener("click", () => pick(o.value, true));
      b.addEventListener("keydown", (ev) => {
        const i = options.findIndex((x) => x.value === o.value);
        const next = ev.key === "ArrowRight" || ev.key === "ArrowDown" ? (i + 1) % options.length : ev.key === "ArrowLeft" || ev.key === "ArrowUp" ? (i - 1 + options.length) % options.length : -1;
        if (next >= 0) { ev.preventDefault(); pick(options[next]!.value, true); }
      });
      return b;
    }));
    if (focus) el.querySelector<HTMLButtonElement>(`[data-value="${focus}"]`)?.focus();
  };
  const pick = (v: string, focus: boolean) => { const changed = v !== current; current = v; draw(focus ? v : undefined); if (changed) onChange(v); };
  draw();
  return { el, set: (v) => { current = v; draw(); } };
}

export function mountViewer(host: HTMLElement, o: ViewerOptions): ViewerHandle {
  const id = o.idPrefix;
  const firstId = (o.pages.find((p) => p.src === o.siteSrc) ?? o.pages[0])?.id ?? ""; // the site opens on its home page, wherever the list puts it
  const st: ViewerState = { mode: "site", device: "desktop", fit: "fit", tiles: "m", pageId: firstId, ...o.state };
  if (!o.pages.some((p) => p.id === st.pageId)) st.pageId = firstId;
  let dead = false;
  const emit = () => o.onState?.({ ...st });

  const stage = h("div", { class: "pv-stage", id: `${id}-stage` });
  const list = h("ul", { class: "pv-pagelist", "aria-label": "Pages of this site" });
  const tileBar = h("div", { class: "pv-tilebar" });
  const modeBar = choice("Preview mode", [{ value: "site", label: o.liveLabel }, { value: "pages", label: "All pages" }], st.mode, (v) => { st.mode = v as ViewerState["mode"]; emit(); draw(); }, { control: "mode" });
  const deviceBar = choice("Preview size", [{ value: "desktop", label: "Desktop" }, { value: "mobile", label: "Mobile" }], st.device, (v) => { st.device = v as ViewerState["device"]; emit(); draw(); }, { control: "device" });
  const fitBar = choice("Preview zoom", [{ value: "fit", label: "Fit to width" }, { value: "actual", label: "Actual size" }], st.fit, (v) => { st.fit = v as ViewerState["fit"]; emit(); scale(); }, { control: "zoom" });
  const tilesBar = choice("Tile size", [{ value: "s", label: "Small" }, { value: "m", label: "Medium" }, { value: "l", label: "Large" }], st.tiles, (v) => { st.tiles = v as ViewerState["tiles"]; emit(); draw(); }, { control: "tiles" });
  const toolbar = h("div", { class: "pv-toolbar" }, modeBar.el, deviceBar.el, fitBar.el, ...(o.extra ?? []));
  const badge = o.kind === "demo" ? h("span", { class: "pill neutral pv-kind", dataset: { kind: "demo" } }, "Sample data") : h("span", { class: "pill ok pv-kind", dataset: { kind: "client" } }, "Client build");
  const note = h("div", { class: `pv-note pv-note--${o.kind}`, role: "note", id: `${id}-note` }, badge, " ", o.notice);

  const current = (): ViewerPage | undefined => o.pages.find((p) => p.id === st.pageId);
  const frameTitle = (p: ViewerPage | undefined) => `${o.name}: ${p?.title ?? "page"}`;

  function drawList() {
    list.replaceChildren(...o.pages.map((p) => h("li", null,
      h("button", { type: "button", class: `pv-page${p.id === st.pageId ? " on" : ""}`, "aria-current": p.id === st.pageId ? "page" : false, dataset: { pageId: p.id }, on: { click: () => show(p.id) } }, h("span", null, p.title), p.group ? h("span", { class: "muted" }, p.group) : null))));
  }

  function liveStage() {
    const mobile = st.device === "mobile";
    stage.className = `pv-stage pv-stage--live pv-stage--${mobile ? "mobile" : "desktop"}`;
    stage.dataset.mode = "site";
    const frame = h("iframe", { id: `${id}-frame`, class: "pv-frame", src: current()?.src ?? o.siteSrc, title: frameTitle(current()), sandbox: "", referrerpolicy: "no-referrer", dataset: { mode: "site", device: st.device, preview: "frame" } });
    stage.replaceChildren(h("div", { class: "pv-device", css: { "--pv-w": `${mobile ? MOBILE_W : DESKTOP_W}px` } }, frame));
    scale();
  }

  function tilesStage() {
    stage.className = "pv-stage pv-stage--pages";
    stage.dataset.mode = "pages";
    const mobile = st.device === "mobile";
    const fw = mobile ? MOBILE_W : DESKTOP_W, fh = TILE_FRAME_H[st.device];
    stage.replaceChildren(h("ul", { class: "pv-tiles", css: { "--pv-cols": TILE_COLS[st.tiles] }, "aria-label": "All pages" },
      ...o.pages.map((p) => h("li", { class: "pv-tile", dataset: { pageId: p.id } },
        h("div", { class: "pv-tile__head" }, h("strong", null, p.title), p.group ? h("span", { class: "muted" }, p.group) : null),
        h("div", { class: "pv-tile__view", css: { "--pv-fw": `${fw}px`, "--pv-fh": `${fh}px`, "--pv-ar": `${fw} / ${fh}` }, "aria-hidden": "true" },
          h("iframe", { class: "pv-tile__frame", src: p.src, title: frameTitle(p), sandbox: "", loading: "lazy", tabindex: "-1", referrerpolicy: "no-referrer", dataset: { pageId: p.id } })),
        h("button", { type: "button", class: "secondary pv-tile__open", dataset: { open: p.id }, "aria-label": `Open ${p.title} in the live view`, on: { click: () => show(p.id) } }, "Open page")))));
    scale();
  }

  function scale() {
    if (dead) return;
    if (st.mode === "site") {
      const dev = stage.querySelector<HTMLElement>(".pv-device");
      if (!dev) return;
      const w = st.device === "mobile" ? MOBILE_W : DESKTOP_W;
      const avail = stage.clientWidth - 24;
      if (avail <= 0) return; // not laid out yet: the observer calls this again
      // "Fit" shows the whole page width at once; "Actual size" is 1:1 and scrolls. A phone-sized frame is always 1:1.
      dev.style.setProperty("--pv-scale", String(st.device === "mobile" || st.fit === "actual" ? 1 : Math.min(1, avail / w)));
      stage.classList.toggle("pv-stage--actual", st.fit === "actual" && st.device === "desktop");
      return;
    }
    stage.querySelectorAll<HTMLElement>(".pv-tile__view").forEach((v) => { const w = v.clientWidth; if (w > 0) v.style.setProperty("--pv-scale", String(w / parseFloat(v.style.getPropertyValue("--pv-fw")))); });
  }

  function draw() {
    if (dead) return;
    const pagesMode = st.mode === "pages";
    fitBar.el.hidden = pagesMode || st.device === "mobile";
    tilesBar.el.hidden = !pagesMode;
    tileBar.replaceChildren(tilesBar.el);
    tileBar.hidden = !pagesMode;
    if (pagesMode) tilesStage(); else liveStage();
    drawList();
  }

  function show(pageId: string) {
    st.pageId = pageId;
    st.mode = "site";
    modeBar.set("site");
    emit();
    draw();
    stage.scrollIntoView?.({ block: "nearest" });
  }

  function expire() {
    if (dead) return;
    stage.className = "pv-stage pv-stage--expired";
    stage.replaceChildren(h("div", { class: "pv-expired", role: "status", dataset: { expired: "true" } }, o.expiredMessage ?? "The preview has expired."));
    list.replaceChildren();
    dead = true;
  }

  const el = h("div", { class: "pv", dataset: { viewer: o.kind } }, note, toolbar, tileBar, h("div", { class: "pv-grid" }, h("div", { class: "pv-main" }, stage), h("nav", { class: "pv-side", "aria-label": "Included pages" }, h("h3", null, "Included pages"), list)));
  host.replaceChildren(el);
  draw();
  requestAnimationFrame(scale);
  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver === "function") { ro = new ResizeObserver(() => scale()); ro.observe(stage); }
  const onResize = () => { if (el.isConnected) scale(); else window.removeEventListener("resize", onResize); };
  window.addEventListener("resize", onResize);
  return { el, state: () => ({ ...st }), show, expire, destroy: () => { dead = true; ro?.disconnect(); window.removeEventListener("resize", onResize); } };
}

/** A full-size viewer over the current page. The page underneath is left exactly as it was, so closing returns to the same place and the same form state. */
export function openViewerOverlay(o: ViewerOptions & { title: string; onClose?: () => void }): { close(): void; handle: ViewerHandle } {
  const opener = document.activeElement as HTMLElement | null;
  const body = h("div", { class: "pv-overlay__body" });
  const close = () => { handle.destroy(); document.removeEventListener("keydown", onKey, true); overlay.remove(); document.body.classList.remove("pv-lock"); opener?.focus?.(); o.onClose?.(); };
  const closeBtn = h("button", { type: "button", class: "secondary pv-overlay__close", id: `${o.idPrefix}-close`, on: { click: close } }, "← Back to the build");
  const overlay = h("div", { class: "pv-overlay", role: "dialog", "aria-modal": "true", "aria-label": o.title, dataset: { overlay: o.idPrefix } },
    h("div", { class: "pv-overlay__bar" }, closeBtn, h("strong", { class: "pv-overlay__title" }, o.title)), body);
  const onKey = (ev: KeyboardEvent) => {
    if (ev.key === "Escape") { ev.stopPropagation(); close(); return; }
    if (ev.key !== "Tab") return;
    const f = [...overlay.querySelectorAll<HTMLElement>("button, a[href], [tabindex]:not([tabindex='-1'])")].filter((x) => !x.hasAttribute("disabled") && x.offsetParent !== null);
    if (!f.length) return;
    const first = f[0]!, last = f[f.length - 1]!;
    if (ev.shiftKey && document.activeElement === first) { ev.preventDefault(); last.focus(); } else if (!ev.shiftKey && document.activeElement === last) { ev.preventDefault(); first.focus(); }
  };
  document.body.append(overlay);
  document.body.classList.add("pv-lock");
  const handle = mountViewer(body, o);
  document.addEventListener("keydown", onKey, true);
  closeBtn.focus();
  return { close, handle };
}
